/* eslint-disable @typescript-eslint/no-confusing-void-expression */
import React, { type FC, useState, useEffect, useMemo } from "react";
import {
  type TrialPlotsPopupProps,
  type RowsColumnsType,
  type Plots,
  type TrialPlot,
} from "../../../../pages/new-trail/types";
import { Button } from "syngenta-digital-cropwise-react-ui-kit";
import { Flex, InputNumber, Popover } from "antd";
import { usePlotCardState } from "../utils";
import { CloseOutlined } from "@ant-design/icons";
import {
  clearStripLinesCache,
  clearStripLinesFromMap,
} from "../../../../maps/components/MapComponent/utils";
import { useNewTrialState } from "../../../../pages/new-trail/hooks/NewTrialStateProvider";
import {
  getGridLinesInfo,
  calculateSmartStripOffsets,
  getStripSnapPositions,
  type GridLinesInfo,
} from "../../../../maps/utils/abLine";
import {
  calculateStripAreas,
  generateParallelLines,
} from "../../../../maps/utils/parallelLinesLayer";
import useFormHandling from "../../../../pages/new-trail/hooks/useFormHandling";
import { restrictToIntegersOnly } from "../../../../pages/create-protocol/utils";
import { toast } from "react-toastify";

const DEFAULT_PLOT_WIDTH = 50;
const DEFAULT_PLOT_HEIGHT = 50;
// Used only when the field/grid geometry can't be analysed yet.
const DEFAULT_MAX_STRIPS = 10;

export const StripsPopup: FC<TrialPlotsPopupProps> = ({
  openPopoverKey,
  onHandleData,
  children,
  val = 0,
  indexKey,
}) => {
  const { popoverState, setPopoverState } = usePlotCardState();
  const { newTrialState, trialProtocolData, setNewTrialState, trialInfo } =
    useNewTrialState();
  const { fieldData } = useFormHandling();

  // Monitor trial plot geometry updates for validation
  useEffect(() => {
    // Monitor strip group geometry updates (new structure: one group with 1×N grid of plots)
    const stripGroups =
      newTrialState.trial_plots?.filter(
        (tp) => tp.plot.isStripPlot && (tp.plot.totalStrips ?? 0) > 0
      ) || [];

    if (stripGroups.length > 0) {
      // Monitoring strip group geometry
    }
  }, [newTrialState.trial_plots]);

  const [stripsCount, setStripsCount] = useState<number>(2);
  const [stripOffsets, setStripOffsets] = useState<number[]>([0]);

  const [inputValue, setInputValue] = useState<RowsColumnsType>({
    rows: 0,
    columns: 0,
    plotWidth: trialProtocolData?.plot_recommended_size?.width,
    plotHeight: trialProtocolData?.plot_recommended_size?.length,
  });

  // Helper function to extract polygon from trial state
  const getPolygonFromTrialState = (): any => {
    try {
      // Get the selected field from fieldData based on trialInfo.field_ids
      const selectedFieldId = trialInfo?.field_ids?.[0];
      if (!selectedFieldId || !fieldData || !Array.isArray(fieldData)) {
        return null;
      }

      const selectedField = fieldData.find(
        (field: any) => field.id === selectedFieldId
      );
      if (!selectedField?.geometry) {
        return null;
      }

      return selectedField.geometry;
    } catch (error) {
      console.error("🎯 StripsPopup: Error extracting polygon:", error);
      return null;
    }
  };

  // Maximum strips the field can hold. A strip line may sit only on a machinery
  // grid line or a grey (half-spacing) line, so N strips need N-1 such positions:
  // max strips = (number of grid+grey positions) + 1.
  const maxStrips = useMemo(() => {
    const selectedAbLine = newTrialState?.ab_line?.find(
      (abLine: any) => abLine.selectedForStrips
    );
    const polygon = getPolygonFromTrialState();
    if (!selectedAbLine?.geojson?.geometry?.coordinates || !polygon) {
      return DEFAULT_MAX_STRIPS;
    }
    try {
      const gridInfo: GridLinesInfo = getGridLinesInfo(
        selectedAbLine.geojson.geometry.coordinates[0],
        selectedAbLine.geojson.geometry.coordinates[1],
        polygon,
        selectedAbLine.grid_width ?? 50
      );
      const positions = getStripSnapPositions(gridInfo);
      return positions.length > 0 ? positions.length + 1 : DEFAULT_MAX_STRIPS;
    } catch (error) {
      console.warn("🎯 StripsPopup: Failed to compute max strips", error);
      return DEFAULT_MAX_STRIPS;
    }
  }, [newTrialState?.ab_line, fieldData, trialInfo?.field_ids]);

  // Generate strip offsets based on strips count using smart grid-based calculation
  const generateStripOffsets = (count: number): number[] => {
    // Input validation - minimum is 2 strips
    if (!count || count < 2) {
      console.warn(
        "🎯 StripsPopup: Invalid count for generateStripOffsets:",
        count,
        "- defaulting to 2"
      );
      return [0]; // Default: 2 strips = AB line only (creates 2 strips with 1 line)
    }

    // Get selected AB line
    const selectedAbLine = newTrialState?.ab_line?.find(
      (abLine: any) => abLine.selectedForStrips
    );

    if (!selectedAbLine) {
      console.warn("🎯 StripsPopup: No AB line selected for strips");
      return [0];
    }

    // Get polygon for grid analysis
    const polygon = getPolygonFromTrialState();
    if (!polygon) {
      console.warn(
        "🎯 StripsPopup: No polygon found, falling back to simple spacing"
      );
      // Fallback to simple spacing if no polygon available
      const spacing = selectedAbLine.grid_width ?? 50;
      const offsets = [];
      for (let i = 1; i < count; i++) {
        offsets.push(i * spacing);
      }
      return offsets;
    }

    try {
      // Smart grid-based calculation
      const gridWidth = selectedAbLine.grid_width ?? 50; // Fallback to 50m if undefined
      const gridInfo: GridLinesInfo = getGridLinesInfo(
        selectedAbLine.geojson.geometry.coordinates[0],
        selectedAbLine.geojson.geometry.coordinates[1],
        polygon,
        gridWidth
      );

      const smartOffsets = calculateSmartStripOffsets(count, gridInfo);

      return smartOffsets;
    } catch (error) {
      // Fallback to simple spacing on error
      const spacing = selectedAbLine.grid_width ?? 50;
      const offsets = [];
      for (let i = 1; i < count; i++) {
        offsets.push(i * spacing);
      }

      return offsets;
    }
  };

  // Load existing strip configuration when popup opens
  useEffect(() => {
    if (popoverState[openPopoverKey] === val) {
      const existingConfig = newTrialState?.stripsData;

      if (existingConfig?.isStripMode) {
        // Loading existing strips configuration

        // Validate and restore existing configuration
        if (existingConfig.count >= 2 && existingConfig.offsets?.length > 0) {
          // Check if AB-line has changed - if so, regenerate offsets instead of using old ones
          const selectedAbLine = newTrialState?.ab_line?.find(
            (abLine: any) => abLine.selectedForStrips
          );

          if (selectedAbLine) {
            // Regenerate offsets for current AB-line instead of using old ones
            const newOffsets = generateStripOffsets(existingConfig.count);
            setStripsCount(existingConfig.count);
            setStripOffsets(newOffsets);
          } else {
            // No AB-line selected, use cached offsets
            setStripsCount(existingConfig.count);
            setStripOffsets(existingConfig.offsets);
          }

          // Restored strips popup state
        } else {
          // Invalid existing strips configuration, using defaults
        }
      } else {
        // No existing config or not in strip mode - initialize defaults
        setStripsCount(2);
        setStripOffsets([0]);
      }
    }
  }, [popoverState, openPopoverKey, val, newTrialState?.stripsData]);

  // Update offsets when strips count changes
  useEffect(() => {
    // Only regenerate offsets if we have a selected AB-line
    const selectedAbLine = newTrialState?.ab_line?.find(
      (abLine: any) => abLine.selectedForStrips
    );

    if (selectedAbLine) {
      const newOffsets = generateStripOffsets(stripsCount);
      setStripOffsets(newOffsets);
    }
  }, [stripsCount]);

  // Update offsets when AB line selection changes
  useEffect(() => {
    const selectedAbLine = newTrialState?.ab_line?.find(
      (abLine: any) => abLine.selectedForStrips
    );

    if (selectedAbLine) {
      // AB line selection changed, regenerating offsets
      const newOffsets = generateStripOffsets(stripsCount);
      setStripOffsets(newOffsets);
    }
  }, [newTrialState?.ab_line]);

  useEffect(() => {
    setInputValue((oldValue) => {
      return {
        ...oldValue,
        plotWidth: trialProtocolData?.plot_recommended_size?.width,
        plotHeight: trialProtocolData?.plot_recommended_size?.length,
      };
    });
  }, [trialProtocolData]);

  const getData = (): Plots[] => {
    const getObjectData = newTrialState?.trial_plots?.map(
      (plotData: TrialPlot, i: number) => {
        if (indexKey === i) {
          return plotData.plot;
        }
        return plotData.plot;
      }
    );
    // console.log( getObjectData)
    return getObjectData;
  };

  const handleOpenChange = (key: string): void => {
    setPopoverState((prevState: Record<string, number | null>) => ({
      ...prevState,
      [key]: val,
    }));
    if (openPopoverKey === "openEditPopover") {
      setInputValue({
        rows: getData()[indexKey as number].rows,
        columns: getData()[indexKey as number].columns,
        plotWidth: getData()[indexKey as number].column_size,
        plotHeight: getData()[indexKey as number].row_size,
      });
      setNewTrialState((prevState: any) => {
        return {
          ...prevState,
          trial_plots: prevState.trial_plots.map(
            (plotData: TrialPlot, i: number) => {
              if (indexKey === i) {
                return {
                  ...plotData,
                  plot: {
                    ...plotData.plot,
                    isEditing: false,
                  },
                };
              }
              return plotData;
            }
          ),
        };
      });
    }
  };

  const handleApplydata = (): void => {
    // Validate inputs - minimum 2 strips
    if (stripsCount < 2) {
      // Console error removed
      return;
    }

    // Validate AB-line selection for strips
    const selectedAbLineIndex =
      newTrialState?.ab_line?.findIndex((ab: any) => ab.selectedForStrips) ??
      -1;
    const selectedAbLine =
      selectedAbLineIndex >= 0
        ? newTrialState?.ab_line?.[selectedAbLineIndex]
        : null;

    if (selectedAbLineIndex < 0 || !selectedAbLine) {
      // Console error removed
      alert("Please select an A/B line before applying strip configuration.");
      return;
    }

    // The user can type any value; the field's max is only enforced here, on
    // Apply, so they can freely edit and re-apply a smaller number.
    if (stripsCount > maxStrips) {
      toast.error(`Only ${maxStrips} strips can be created for this field.`);
      return;
    }

    if (!stripOffsets || stripOffsets.length === 0) {
      // Console error removed
      return;
    }

    // Capacity check: recompute the strips with the band engine and require every
    // requested strip to be non-empty. Equal-width offsets can push a band into a
    // MultiPolygon gap or beyond a small field; in that case reject rather than
    // silently producing fewer/empty strips.
    const fieldForValidation = getPolygonFromTrialState();
    if (fieldForValidation) {
      try {
        const stripLines = generateParallelLines(
          selectedAbLine as any,
          stripOffsets,
          fieldForValidation
        );
        const { strips } = calculateStripAreas(stripLines, fieldForValidation);
        const nonEmptyStrips = strips.filter((strip) => strip.area > 0).length;
        if (nonEmptyStrips !== stripsCount) {
          toast.error(
            `Cannot create ${stripsCount} strips in this field. Try a smaller number.`
          );
          return;
        }
      } catch (error) {
        console.warn("Strip capacity validation failed:", error);
      }
    }

    // Ensure plot dimensions are valid
    const validPlotWidth = Math.max(
      inputValue.plotWidth ?? DEFAULT_PLOT_WIDTH,
      1
    );
    const validPlotHeight = Math.max(
      inputValue.plotHeight ?? DEFAULT_PLOT_HEIGHT,
      1
    );

    // Clear existing trial plots and create new strip-based trial data
    setNewTrialState((prevState: any) => {
      // Use the validated AB line selection
      const abLineWidth = selectedAbLine?.grid_width ?? 50;

      // Create a SINGLE GROUP with MULTIPLE PLOTS (like 1×3 grid where each plot is a strip)
      // Similar to regular plots: one group contains multiple individual plots
      const baseTimestamp = new Date().getTime();

      // Create the main plot group with proper grid structure
      const stripPlotGroup = {
        id: baseTimestamp,
        key: "Trial Plots",
        name: "Strips Group",
        visible: true,
        rows: 1, // Single row
        columns: stripsCount, // One column per strip (1×3 for 3 strips)
        column_size: validPlotWidth,
        row_size: validPlotHeight,
        plotWidth: validPlotWidth,
        plotHeight: validPlotHeight,
        // Mark as strip plot group
        isStripPlot: true,
        geometrySource: "manual",
        // The main group geometry - will be updated with actual strip geometries
        geojson: {
          type: "Feature",
          geometry: {
            type: "Point", // Start as Point, will become MultiPolygon when updated
            coordinates: [0, 0], // Placeholder coordinates
          },
        },
        // Add strip-specific metadata to the group
        stripIndices: Array.from({ length: stripsCount }, (_, i) => i), // [0, 1, 2] for 3 strips
        totalStrips: stripsCount,
      };

      // Create the single group (no replicants needed - strips are individual plots within the group)
      const stripGroup = {
        plot: stripPlotGroup,
        replicants: [], // Empty - strips are handled as individual plots within the group
      };

      // Force recalculation of any existing strip plots
      const existingStripPlots = prevState.trial_plots.filter(
        (tp: any) => tp.plot.isStripPlot
      );
      if (existingStripPlots.length > 0) {
        // Console log removed
      }

      // Validate strip group creation
      // Console log removed
      // Strip group validation

      return {
        ...prevState,
        trial_plots: [stripGroup], // Single group representing 1×N grid of strip plots
        // Consolidated stripsData - single source of truth
        stripsData: {
          isStripMode: true,
          appliedFromPopup: true, // Flag to indicate applied from popup
          count: stripsCount,
          selectedAbLineIndex,
          offsets: stripOffsets,
          spacing:
            stripOffsets.length > 1
              ? stripOffsets[1] - stripOffsets[0]
              : abLineWidth,
          createdAt: new Date().toISOString(),
          version: "1.0",
          // Store selected AB-line details to avoid dependency issues
          selectedAbLine: {
            name: selectedAbLine.name,
            gridWidth: abLineWidth,
            geojson: selectedAbLine.geojson,
          },
        },
      };
    });

    // Close the popup and reset state
    setPopoverState((prevState: Record<string, number | null>) => ({
      ...prevState,
      openPopover: null,
      openEditPopover: null,
    }));

    // Console log removed

    // Clear strip lines cache and map to ensure completely fresh processing
    // Note: This only clears strip lines, NOT the grey AB line grid
    // Console log removed
    clearStripLinesCache();
    clearStripLinesFromMap();

    // Force re-render of AB line grid with grey lines after a short delay
    // This ensures the grey lines are redrawn after clearing strip lines
    setTimeout(() => {
      // Console log removed
      clearStripLinesCache();

      // Trigger AB line grid redraw by updating AB line state
      // This will cause updateABWidth to be called with drawGreyLines: true
      setNewTrialState((prevState: any) => ({
        ...prevState,
        // Force update to trigger AB line redraw
        ab_line: prevState.ab_line.map((abLine: any) => ({
          ...abLine,
          // Add a timestamp to force re-render without changing actual data
          _lastUpdate: Date.now(),
        })),
      }));
    }, 150);
  };

  const handleClosePopover = (): void => {
    setInputValue({
      rows: 0,
      columns: 0,
      plotHeight:
        trialProtocolData?.plot_recommended_size?.length ?? DEFAULT_PLOT_HEIGHT,
      plotWidth:
        trialProtocolData?.plot_recommended_size?.width ?? DEFAULT_PLOT_WIDTH,
    });
    setPopoverState((prevState: Record<string, number | null>) => ({
      ...prevState,
      openPopover: null,
      openEditPopover: null,
    }));
  };

  const titleContent = (
    <div className="popover-title">
      {/* <span>{formatTranslation(t("newTrial.plotDesign.trialPlots"))}</span> */}
      <span> Strip Setup </span>

      <Button className="close-btn" onClick={handleClosePopover}>
        <CloseOutlined />
      </Button>
    </div>
  );

  const content = (
    <div className="buffer-zone-popup">
      {/* <p className="desc">Configure strip for this trial plot.</p> */}
      <div className="zone-input-sec">
        <Flex vertical={true} gap={16}>
          <div>
            <label
              htmlFor="strips-count"
              style={{ color: "#696F88", fontSize: "12px" }}
            >
              Amount of strip
            </label>
            <div className="input-main">
              <InputNumber
                data-testid="strips-input"
                value={stripsCount}
                min={2}
                // No `max` here on purpose: the user may type any value; the
                // field's max is validated on Apply (see handleApplydata) so the
                // number isn't capped/toasted while they are still editing it.
                precision={0}
                onChange={(value) => {
                  if (value && value >= 2) {
                    setStripsCount(value);
                  }
                }}
                onKeyDown={(e: any) => {
                  restrictToIntegersOnly(e);
                }}
                controls={true}
                placeholder="Enter number of strip (min: 2)"
                style={{ width: "100%" }}
              />
            </div>
          </div>
        </Flex>
      </div>
      {/* Table commented commented */}
      {/* {newTrialState?.stripsData?.stripDetails && (
        <div>
          <table className="stripe-preview-table">
            <thead>
              <tr>
                <td className="t-header">Strip</td>
                <td className="t-header">Width</td>
                <td className="t-header">Length</td>
                <td className="t-header">Area</td>
              </tr>
            </thead>
            <tbody>
              {newTrialState?.stripsData?.stripDetails?.map((item, index) => (
                <tr key={index}>
                  <td> Strip {index + 1}</td>
                  <td style={{ color: "#696F88" }}>
                    {item?.stripWidth?.toFixed(2)}m
                  </td>
                  <td style={{ color: "#696F88" }}>
                    {item?.stripLength?.toFixed(2)}m
                  </td>
                  <td style={{ color: "#696F88" }}>
                    {formatAreaInHectares(item?.area)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )} */}
      <Flex gap={8}>
        <Button
          style={{ flex: 1, marginTop: "24px" }}
          onClick={handleClosePopover}
        >
          Cancel
        </Button>
        <Button
          style={{ flex: 1 }}
          className={"apply-btn apply-trial-btn"}
          disabled={stripsCount === 0 || stripsCount < 2}
          onClick={handleApplydata}
        >
          Apply
        </Button>
      </Flex>
    </div>
  );

  return (
    <>
      {popoverState[openPopoverKey] === val && (
        <Button
          onClick={handleClosePopover}
          className="popover-backdrop"
        ></Button>
      )}
      <Popover
        content={content}
        title={titleContent}
        placement="right"
        trigger={"click"}
        open={popoverState[openPopoverKey] === val}
        onOpenChange={() => {
          handleOpenChange(openPopoverKey);
        }}
      >
        {children}
      </Popover>
    </>
  );
};
